const STORAGE_KEY = 'd88_pending_referral_code';
const CHANNEL_KEY = 'd88_pending_referral_channel';
const CAMPAIGN_KEY = 'd88_pending_campaign_slug';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/** Valid stored / submitted referral code, or null if empty/invalid. */
export function normalizeReferralCode(raw: string | null | undefined): string | null {
  const v = (raw || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return v.length >= 4 && v.length <= 32 ? v : null;
}

/** Sanitize while typing (does not require min length). */
export function sanitizeReferralCodeInput(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 32);
}

function normalizeRef(raw: string | null | undefined): string | null {
  return normalizeReferralCode(raw);
}

/** Persist ?ref= / ?op= / ?referral= and optional ?camp= / ?ch= from landing pages. */
export function captureReferralFromSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const ref = normalizeRef(params.get('ref') || params.get('referral') || params.get('op'));
  const channel = (params.get('ch') || params.get('channel') || '').trim().toLowerCase();
  const campaign = (params.get('camp') || params.get('campaign') || '').trim();

  if (channel) {
    try { sessionStorage.setItem(CHANNEL_KEY, channel); } catch { /* ignore */ }
  }
  if (campaign) {
    try { sessionStorage.setItem(CAMPAIGN_KEY, campaign); } catch { /* ignore */ }
  }

  if (!ref) return null;
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code: ref, at: Date.now(), channel: channel || undefined, campaign: campaign || undefined })
    );
  } catch {
    /* ignore quota / private mode */
  }
  return ref;
}

export function getPendingReferralChannel(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { channel?: string };
      if (parsed?.channel) return parsed.channel;
    }
    return sessionStorage.getItem(CHANNEL_KEY);
  } catch {
    return null;
  }
}

export function getPendingCampaignSlug(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { campaign?: string };
      if (parsed?.campaign) return parsed.campaign;
    }
    return sessionStorage.getItem(CAMPAIGN_KEY);
  } catch {
    return null;
  }
}

export function clearPendingCampaignData(): void {
  try {
    sessionStorage.removeItem(CHANNEL_KEY);
    sessionStorage.removeItem(CAMPAIGN_KEY);
  } catch { /* ignore */ }
}

export function getPendingReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { code?: string; at?: number };
    if (!parsed?.code) return null;
    if (parsed.at && Date.now() - parsed.at > MAX_AGE_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return normalizeRef(parsed.code);
  } catch {
    return null;
  }
}

export function clearPendingReferralCode(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    clearPendingCampaignData();
  } catch {
    /* ignore */
  }
}

/** Build /register URL preserving ref + optional redirect. */
export function registerPath(opts?: { redirect?: string }): string {
  const params = new URLSearchParams();
  const ref = getPendingReferralCode();
  if (ref) params.set('ref', ref);
  if (opts?.redirect) params.set('redirect', opts.redirect);
  const q = params.toString();
  return q ? `/register?${q}` : '/register';
}
